/**
 * Season Utilities (Client-Side)
 * Helper functions for volleyball season date calculations
 */

/**
 * Generate volleyball season options for the next N years
 * @param {number} count - Number of seasons to generate (default: 10)
 * @returns {Array<string>} Array of season strings (e.g., ["2024-2025", "2025-2026"])
 */
export function generateSeasonOptions(count = 10) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-indexed
  
  // If we're past August (month 8), the current season has already started
  const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;
  
  const seasons = [];
  for (let i = 0; i < count; i++) {
    const year1 = startYear + i;
    const year2 = year1 + 1;
    seasons.push(`${year1}-${year2}`);
  }
  
  return seasons;
}

/**
 * Calculate current volleyball season based on today's date
 * @returns {string} Current season string (e.g., "2024-2025")
 */
export function getCurrentSeason() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed
  
  // Season runs from September (month 8) to June (month 5)
  // If we're in July or August (months 6-7), we're between seasons
  if (currentMonth >= 8) {
    // September or later - current season started this year
    return `${currentYear}-${currentYear + 1}`;
  } else if (currentMonth <= 5) {
    // January to June - current season started last year
    return `${currentYear - 1}-${currentYear}`;
  } else {
    // July-August - between seasons, return upcoming season
    return `${currentYear}-${currentYear + 1}`;
  }
}

/**
 * Convert season string to start and end dates
 * @param {string} seasonString - Season in format "2024-2025"
 * @returns {Object|null} Object with start_date and end_date in ISO format (yyyy-MM-dd)
 */
export function getSeasonDates(seasonString) {
  if (!seasonString || typeof seasonString !== 'string') {
    return null;
  }
  
  const match = seasonString.match(/^(\d{4})-(\d{4})$/);
  if (!match) {
    return null;
  }
  
  const startYear = parseInt(match[1]);
  const endYear = parseInt(match[2]);
  
  // Validate that end year is start year + 1
  if (endYear !== startYear + 1) {
    return null;
  }
  
  return {
    start_date: `${startYear}-09-01`,
    end_date: `${endYear}-06-30`
  };
}

/**
 * Validate season string format
 * @param {string} seasonString - Season string to validate
 * @returns {boolean} True if valid format
 */
export function isValidSeason(seasonString) {
  if (!seasonString || typeof seasonString !== 'string') {
    return false;
  }
  
  const match = seasonString.match(/^(\d{4})-(\d{4})$/);
  if (!match) {
    return false;
  }
  
  const startYear = parseInt(match[1]);
  const endYear = parseInt(match[2]);
  
  return endYear === startYear + 1;
}

export default {
  generateSeasonOptions,
  getCurrentSeason,
  getSeasonDates,
  isValidSeason
};